import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Inputs from '../../../components/Inputs';
import useUpdateView from './FieldSlideOut/useUpdateView';
import useTrackSettingsChanged from './FieldSlideOut/useTrackFieldSettingsChanged';
import { GEO_POINT_CELL } from '../../../components/Grid/constants';
import DatasetDefinitionsContext from '../../../contexts/DatasetDefinitionsContext';
import { QUALIFIES_FOR_BONUS_FIELD } from '../../../constants';

const getBaseCellTypes = (field: FleetOps.Field): FleetOps.CellType[] => {
  switch (field.type) {
    case 'long':
    case 'float':
    case 'int':
      return [
        'Currency',
        'Percentage',
        'Number',
        'Colour Coded Number',
        'Status Flag',
      ];
    case 'date':
      return ['Date'];
    case 'geo_point':
      return [GEO_POINT_CELL];
    case 'boolean':
      return ['Text', 'Boolean Icon'];
    case 'text':
    default:
      return ['Text'];
  }
};

const NUMBER_CELLS = ['Currency', 'Number', 'Percentage'];

export const buildFormatting = (
  cellType: FleetOps.CellType,
  currencyType?: CurrencyType,
): MetricFormatting => {
  switch (cellType) {
    case 'Currency':
      return {
        prefix: 'currency',
        postfix: undefined,
        currencyType,
        precision: 2,
        positiveDeltaIsGood: true,
      };
    case 'Number':
      return {
        prefix: undefined,
        postfix: undefined,
        precision: 2,
        positiveDeltaIsGood: true,
      };
    case 'Percentage':
      return {
        prefix: undefined,
        postfix: 'percentage',
        precision: 2,
        positiveDeltaIsGood: true,
      };
    case 'Status Flag':
    case 'Date':
    case 'Text':
    default:
      return {
        prefix: undefined,
        postfix: undefined,
        precision: undefined,
        positiveDeltaIsGood: undefined,
      };
  }
};

const useCellTypeOptions = ({
  field,
  fieldView,
  baseView,
}: {
  field: FleetOps.Field;
  fieldView: FleetOps.BaseViewField;
  baseView: FleetOps.BaseView;
}) => {
  const { datasets, getBonusProgram } = useContext(DatasetDefinitionsContext);
  const updateView = useUpdateView({ field, baseView });
  const cellTypes = useMemo((): FleetOps.CellType[] => {
    const dataset = datasets.find((d) => d.type === baseView.type);
    const isBonusDataset = dataset && dataset.datasetType === 'bonus';
    const baseTypes = getBaseCellTypes(field);

    if (!dataset || !isBonusDataset) {
      return baseTypes;
    }

    const bonusProgram = getBonusProgram(dataset.type);
    if (!bonusProgram) {
      return baseTypes;
    }
    if (field.field === QUALIFIES_FOR_BONUS_FIELD) {
      return ['Bonus Eligibility Status', ...baseTypes];
    }

    const isStatusField = bonusProgram.criteriaFields.some(
      (f) => f.statusField === field.field,
    );
    if (isStatusField) {
      return ['Bonus Status Field', ...baseTypes];
    }

    const isScoreField = field.field.toLowerCase().includes('score');
    if (isScoreField) {
      return ['Bonus Score Field', 'Bonus Expectation Field', ...baseTypes];
    }

    const allExpectationFields = [
      ...bonusProgram.criteriaFields,
      ...(bonusProgram.qualifierFields ?? []),
      ...(bonusProgram.disqualifierFields ?? []),
    ];
    const isExpectationField = allExpectationFields.some(
      (f) => f.actualField === field.field || f.targetField === field.field,
    );
    if (isExpectationField) {
      return ['Bonus Expectation Field', ...baseTypes];
    }

    const isRewardField = bonusProgram.rewardFields.some(
      (f) =>
        f.rewardValueField === field.field ||
        f.potentialRewardValueField === field.field,
    );
    if (isRewardField) {
      return ['Bonus Reward Field', ...baseTypes];
    }

    return baseTypes;
  }, [baseView.type, datasets, field, getBonusProgram]);
  const trackSettingsChanged = useTrackSettingsChanged({ field, baseView });

  const getCellTypeOptions = useCallback(
    () =>
      cellTypes.map((t) => ({
        label: t,
        value: t,
        onSelected: () => {
          const newView = {
            ...fieldView,
            cellType: t,
            formatting: buildFormatting(t),
            precision:
              NUMBER_CELLS.includes(t) && field.type === 'float'
                ? 2
                : fieldView.precision,
          };

          updateView(newView);
          trackSettingsChanged();
        },
      })),
    [cellTypes, field.type, fieldView, trackSettingsChanged, updateView],
  );
  const [cellTypeOptions, setCellTypeOptions] =
    useState<DropdownOption[]>(getCellTypeOptions);
  useEffect(() => {
    setCellTypeOptions(getCellTypeOptions());
  }, [getCellTypeOptions]);

  return cellTypeOptions;
};

const FieldCellTypeDropdown = ({
  field,
  fieldView,
  baseView,
  testId,
}: {
  field: FleetOps.Field;
  fieldView: FleetOps.BaseViewField;
  baseView: FleetOps.BaseView;
  testId?: string;
}) => {
  const cellTypeOptions = useCellTypeOptions({ field, fieldView, baseView });

  return (
    <Inputs.Dropdown
      options={cellTypeOptions}
      testId={testId}
      selectedLabel={fieldView.cellType ? fieldView.cellType : 'None'}
    />
  );
};

export default FieldCellTypeDropdown;
